import { useState, useContext, useCallback } from 'react';
import { getBeaconAssociations } from '../services/apiServices';
import { feedbackMessages } from '../constants';
import { LoadingContext, useFeedbackContext } from '../context';
import { serviceLogger } from '../utils/serviceLogger';

/**
 * Custom hook to manage beacon data, including fetching and updating beacons.
 *
 *  This hook:
 *  - Fetches beacon associations from the server.
 *  - Manages global and local loading states during requests.
 *  - Utilizes the global FeedbackContext to display error toasts when fetching fails.
 *
 * @returns {Object} An object containing:
 *   - `beacons`: Array of beacon data.
 *   - `fetchBeacons`: Function to re-fetch beacons.
 *   - `beaconsIsloading`: Boolean indicating if beacons are currently being fetched.
 */
const useBeaconsData = () => {
  const [beacons, setBeacons] = useState([]);
  const [beaconsIsloading, setBeaconIsLoading] = useState(false);
  const { setIsLoading } = useContext(LoadingContext);

  const { showToast } = useFeedbackContext();

  /**
   * Fetches beacon associations from the API and updates state.
   * Displays an error message if the request fails.
   */
  const fetchBeacons = useCallback(async () => {
    setIsLoading(true);
    setBeaconIsLoading(true);
    try {
      const data = await getBeaconAssociations();
      setBeacons(data);
    } catch (error) {
      showToast({
        variant: feedbackMessages.ERROR_FETCH_BEACONS.variant,
        title: feedbackMessages.ERROR_FETCH_BEACONS.title,
        copy: feedbackMessages.ERROR_FETCH_BEACONS.copy,
      });
      serviceLogger.error('Error fetching beacons:', error);
    } finally {
      setIsLoading(false);
      setBeaconIsLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return { beacons, fetchBeacons, beaconsIsloading };
};

export default useBeaconsData;
